import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  MessageSquare, Plus, Pencil, Trash2, Search, Zap, Star, ThumbsUp,
  IndianRupee, MapPin, Clock, Building2, Shield, HelpCircle, Tag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { stripMarkdown } from "../utils/stripMarkdown";
import { CopyButton } from "../components/CopyButton";

const CATEGORIES = [
  { value: "price", label: "Price / Budget", icon: IndianRupee, color: "bg-emerald-100 text-emerald-700" },
  { value: "location", label: "Location", icon: MapPin, color: "bg-blue-100 text-blue-700" },
  { value: "possession", label: "Possession / Delay", icon: Clock, color: "bg-amber-100 text-amber-700" },
  { value: "competition", label: "Competition", icon: Building2, color: "bg-violet-100 text-violet-700" },
  { value: "trust", label: "Trust / Builder", icon: Shield, color: "bg-rose-100 text-rose-700" },
  { value: "financing", label: "Financing / Loan", icon: IndianRupee, color: "bg-cyan-100 text-cyan-700" },
  { value: "other", label: "Other", icon: HelpCircle, color: "bg-slate-100 text-slate-700" },
];

const emptyForm = { title: "", category: "price", description: "", tags: "" };

const ObjectionHandlerPage = () => {
  const [objections, setObjections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingObj, setEditingObj] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [generatingId, setGeneratingId] = useState(null);
  const [rebuttalDialogOpen, setRebuttalDialogOpen] = useState(false);
  const [selectedObj, setSelectedObj] = useState(null);
  const [customRebuttal, setCustomRebuttal] = useState("");

  const fetchObjections = async () => {
    try {
      const params = filterCat !== "all" ? `?category=${filterCat}` : "";
      const res = await axios.get(`${API}/decision-support/objections${params}`);
      setObjections(res.data);
    } catch (err) { toast.error("Failed to load objections"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchObjections(); }, [filterCat]);

  const openCreate = () => { setEditingObj(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (obj) => {
    setEditingObj(obj);
    setForm({ title: obj.title, category: obj.category, description: obj.description || "", tags: (obj.tags || []).join(", ") });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Objection title required"); return; }
    const payload = { ...form, tags: form.tags ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : [] };
    try {
      if (editingObj) {
        await axios.put(`${API}/decision-support/objections/${editingObj.objection_id}`, payload);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/decision-support/objections`, payload);
        toast.success("Objection added");
      }
      setDialogOpen(false);
      fetchObjections();
    } catch (err) { toast.error("Failed to save"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this objection?")) return;
    try { await axios.delete(`${API}/decision-support/objections/${id}`); toast.success("Deleted"); fetchObjections(); }
    catch { toast.error("Failed to delete"); }
  };

  const generateRebuttal = async (objId) => {
    setGeneratingId(objId);
    try {
      const res = await axios.post(`${API}/decision-support/objections/${objId}/generate-rebuttal`, {});
      toast.success("AI rebuttal generated");
      fetchObjections();
    } catch (err) { toast.error("AI generation failed"); }
    finally { setGeneratingId(null); }
  };

  const openRebuttalDialog = (obj) => {
    setSelectedObj(obj);
    setCustomRebuttal("");
    setRebuttalDialogOpen(true);
  };

  const addRebuttal = async () => {
    if (!customRebuttal.trim()) return;
    try {
      await axios.post(`${API}/decision-support/objections/${selectedObj.objection_id}/rebuttal`, { rebuttal: customRebuttal });
      toast.success("Rebuttal added");
      setRebuttalDialogOpen(false);
      fetchObjections();
    } catch (err) { toast.error("Failed to add rebuttal"); }
  };

  const rateObjection = async (objId, rating) => {
    try {
      await axios.post(`${API}/decision-support/objections/${objId}/rate`, { rating });
      toast.success("Rated");
      fetchObjections();
    } catch { toast.error("Rating failed"); }
  };

  const filtered = objections.filter(o => o.title?.toLowerCase().includes(searchTerm.toLowerCase()) || o.description?.toLowerCase().includes(searchTerm.toLowerCase()));
  const getCat = (v) => CATEGORIES.find(c => c.value === v) || CATEGORIES[6];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="objection-handler-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Objection Handler</h1>
            <p className="text-sm text-slate-500 mt-1">Manage buyer objections with AI-powered and custom rebuttals</p>
          </div>
          <Button onClick={openCreate} data-testid="create-objection-btn">
            <Plus className="w-4 h-4 mr-2" /> New Objection
          </Button>
        </div>

        {/* Category Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          {CATEGORIES.map((cat) => {
            const count = objections.filter(o => o.category === cat.value).length;
            const Icon = cat.icon;
            return (
              <Card key={cat.value} className={`cursor-pointer transition-shadow hover:shadow-md ${filterCat === cat.value ? "ring-2 ring-blue-400" : ""}`}
                onClick={() => setFilterCat(filterCat === cat.value ? "all" : cat.value)}>
                <CardContent className="p-3 flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cat.color}`}><Icon className="w-4 h-4" /></div>
                  <div><p className="text-xs text-slate-500">{cat.label}</p><p className="text-lg font-bold text-slate-900">{count}</p></div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search objections..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="objection-search" />
        </div>

        {/* Objection Cards */}
        {loading ? <p className="text-center text-slate-400 py-12">Loading...</p> :
        filtered.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-slate-400"><MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No objections found. Add common buyer objections to get started.</p></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((obj) => {
              const cat = getCat(obj.category);
              const Icon = cat.icon;
              return (
                <Card key={obj.objection_id} className="hover:shadow-md transition-shadow" data-testid={`objection-card-${obj.objection_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cat.color}`}><Icon className="w-5 h-5" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900">{obj.title}</h3>
                          <Badge variant="outline" className={`text-xs ${cat.color}`}>{cat.label}</Badge>
                          {obj.effectiveness_rating && (
                            <span className="flex items-center gap-0.5 text-xs text-amber-600"><Star className="w-3 h-3 fill-amber-400" />{obj.effectiveness_rating}</span>
                          )}
                          {obj.usage_count > 0 && <span className="text-xs text-slate-400">Used {obj.usage_count}x</span>}
                        </div>
                        {obj.description && <p className="text-sm text-slate-600 mt-1">{obj.description}</p>}
                        {obj.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {obj.tags.map((t, i) => <Badge key={i} variant="secondary" className="text-xs"><Tag className="w-2.5 h-2.5 mr-1" />{t}</Badge>)}
                          </div>
                        )}

                        {/* AI Rebuttal */}
                        {obj.ai_rebuttal && (
                          <div className="mt-3 bg-blue-50 rounded-lg p-3 border border-blue-100">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs font-medium text-blue-700 flex items-center gap-1"><Zap className="w-3 h-3" /> AI Rebuttal</p>
                              <CopyButton text={stripMarkdown(obj.ai_rebuttal)} label="rebuttal" />
                            </div>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{stripMarkdown(obj.ai_rebuttal)}</p>
                          </div>
                        )}

                        {/* Custom Rebuttals */}
                        {obj.custom_rebuttals?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {obj.custom_rebuttals.map((r, i) => (
                              <div key={i} className="bg-slate-50 rounded px-3 py-2 text-sm text-slate-700 border">
                                <span className="text-xs text-slate-400 font-medium">Custom #{i + 1}: </span>{r}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => generateRebuttal(obj.objection_id)}
                          disabled={generatingId === obj.objection_id} data-testid={`generate-rebuttal-${obj.objection_id}`}>
                          <Zap className="w-3 h-3 mr-1" /> {generatingId === obj.objection_id ? "..." : "AI"}
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openRebuttalDialog(obj)} data-testid={`add-rebuttal-${obj.objection_id}`}>
                          <Plus className="w-3 h-3 mr-1" /> Rebuttal
                        </Button>
                        <div className="flex gap-0.5 mt-1">
                          {[1, 2, 3, 4, 5].map(r => (
                            <button key={r} onClick={() => rateObjection(obj.objection_id, r)} className="p-0.5">
                              <Star className={`w-3 h-3 ${r <= (obj.effectiveness_rating || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-0.5 mt-1">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(obj)}><Pencil className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDelete(obj.objection_id)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="objection-dialog">
            <DialogHeader><DialogTitle>{editingObj ? "Edit Objection" : "New Objection"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Objection *</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Price is too high compared to area average" data-testid="objection-title-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="objection-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Details / Context</label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Buyer's typical reasoning, context, what they compare with..." rows={3} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Tags</label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="Comma-separated: budget, premium, 2bhk" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="objection-save-btn">{editingObj ? "Update" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Custom Rebuttal Dialog */}
        <Dialog open={rebuttalDialogOpen} onOpenChange={setRebuttalDialogOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="rebuttal-dialog">
            <DialogHeader><DialogTitle>Add Custom Rebuttal</DialogTitle></DialogHeader>
            <div className="py-2">
              <p className="text-sm text-slate-600 mb-3">For: <span className="font-medium">{selectedObj?.title}</span></p>
              <Textarea value={customRebuttal} onChange={(e) => setCustomRebuttal(e.target.value)}
                placeholder="Write your custom rebuttal that works well for this objection..." rows={5} data-testid="custom-rebuttal-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRebuttalDialogOpen(false)}>Cancel</Button>
              <Button onClick={addRebuttal} data-testid="save-rebuttal-btn">Add Rebuttal</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ObjectionHandlerPage;
